import { useEffect, useRef, useState } from "react";
import { InputForm } from "../../atoms/input";
import { Modal, ModalBody, ModalFooter } from "../modal";
import { Alert } from "../alert";
import { StandardButton } from "../../atoms/buttons";
import { HorizontalLayout, VerticalLayout } from "../layout";
import { IconAtom2 } from "../../atoms/iconAtom";
import ExpligoWP from "../../assets/expligo_wp2.png";
import ElearningFeed from "./ElearningFeed";
import { getAPI, postAPI } from "../../api/api";
import { Spinner } from "../../atoms/spinner";
import ExpligoCard from "./ExpligoCard";
import CardInfo from "./CardInfo";

export default function TapToLearn() {
  const [active, setActive] = useState(true);
  const [message, setMessage] = useState("");
  const [idNotFound, setIdNotFound] = useState(true);
  const [loading, setLoading] = useState(true);
  const [cardInfo, setCardInfo] = useState({});
  const [elearningFeedData, setElearningFeedData] = useState([]);
  const [waitingForMailCookie, setWaitingForMailCookie] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const hasVisitedRef = useRef(false);

  const getCardsStorage = () => {
    const raw = localStorage.getItem("expligo_cards");
    return raw ? JSON.parse(raw) : {};
  };

  const saveCardsStorage = (cards) => {
    localStorage.setItem("expligo_cards", JSON.stringify(cards));
  };

  const getDeviceToken = () => {
    let token = localStorage.getItem("device_token");
    if (!token) {
      token = crypto.randomUUID ? crypto.randomUUID() : generateUUIDFallback();
      localStorage.setItem("device_token", token);
    }
    return token;
  };

  function getCardInfoFromUrl() {
    const pathParts = window.location.pathname.split("/");
    const uuid = pathParts[pathParts.length - 1] || null;
    const urlCode = new URLSearchParams(window.location.search).get("code");
    const deviceToken = getDeviceToken();

    return { uuid, urlCode, deviceToken };
  }

  function handleError(message) {
    setErrorMessage(message);
    setIdNotFound(true);
    setLoading(false);
  }

  function generateUUIDFallback() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0, v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  useEffect(() => {
    let { uuid, urlCode, deviceToken } = getCardInfoFromUrl();
    const cards = getCardsStorage();

    if (!uuid) {
      return handleError("Ongeldige link. Kaart-ID ontbreekt.");
    }

    const storedCode = cards[uuid];
    const codeToSend = urlCode || storedCode;

    const isNewCard = !storedCode || (urlCode && urlCode !== storedCode);
    setActive(isNewCard);

    if (!hasVisitedRef.current) {
      checkCardId(uuid, codeToSend, deviceToken)
        .finally(() => setLoading(false));
      hasVisitedRef.current = true;
    }
  }, []);

  const checkCardId = async (uuid, code, deviceToken) => {
    const body = {};
    if (code) body.code = code;
    if (deviceToken) body.device_token = deviceToken;

    console.log("Checking card with body:", body);

    const res = await postAPI(`api/cards/check-id/${uuid}`, body);
    console.log("Card check response:", res);
    if (res.status === "ok") {
      setCardInfo(res.data);
      setIdNotFound(false);
      await fetchElearningFeedData(res.data.id_card);

      const codeToStore = code || res.data.code || "";
      storeCardIfNew(res.data, codeToStore);

      setActive(!res.data.access); 
      window.history.replaceState({}, "", window.location.pathname);
    } else {
      setIdNotFound(true);
    }
  };

  function storeCardIfNew(cardInfo, codeUsed) {
    if (!cardInfo?.uuid) return false;
    if (!codeUsed) return false;

    const cards = getCardsStorage();
    const isNewCard = !cards[cardInfo.uuid] || cards[cardInfo.uuid] !== codeUsed;

    if (isNewCard) {
      cards[cardInfo.uuid] = codeUsed;
      saveCardsStorage(cards);
      console.log("Saved card in localStorage:", cards);
    }

    return isNewCard;
  }


  const fetchElearningFeedData = async (id_card) => {
    const res = await getAPI(`api/cards/elearning-feed/${id_card}`);
    if (res.status === "ok") setElearningFeedData(res.data);
  };

  // =========================
  // Handle activation
  // =========================
  const handleActivation = async (info) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!info.email || !emailRegex.test(info.email)) {
      setMessage("Vul alstublieft een geldig e-mailadres in.");
      setTimeout(() => setMessage(""), 3000);
      return;
    }

    const body = { ...info, id_card: cardInfo.id_card };
    const res = await postAPI(`api/cards/activate-card`, body);

    if (res.status === "ok") {
      setWaitingForMailCookie(true);
    }
  };

  if (loading) {
    return (
      <VerticalLayout className="center">
        <Spinner />
      </VerticalLayout>
    );
  }

  if (idNotFound) {
    return <CardIdNotFound errorMessage={errorMessage} />;
  }

  if (cardInfo.owner) {
    return <CardInfo cardInfo={cardInfo} />;
  }

  return (
    <>
      <div className={`tap-to-learn overlay ${active && "active"}`}>
        <ElearningFeed elearningFeedData={elearningFeedData} />
      </div>

      <ActivationModal
        waitingForMailCookie={waitingForMailCookie}
        cardOwnerInfo={cardInfo.lead}
        message={message}
        handleActivation={handleActivation}
        active={active}
      />
    </>
  );
}

const ActivationModal = ({ active, handleActivation, message, cardOwnerInfo = {}, waitingForMailCookie }) => {
  const [cardInfo, setCardInfo] = useState({ ...cardOwnerInfo });
  const dataToSave = () => {
    handleActivation(cardInfo);
  };
   const initialEmail = cardOwnerInfo.email || "";

  return (
    <>
      <Modal height={null} visible={active}>
        <ModalBody style={{ minHeight: "28rem", }}>
          <Header waitingForMailCookie={waitingForMailCookie} />
          {!waitingForMailCookie &&  (
            <>  
           <InputForm
            disabled={initialEmail.trim() !== ""}
            value={cardInfo.email}
            typeInput={(v) => setCardInfo((prev) => ({ ...prev, email: v }))}
            title={"E-mail *"}
            placeholder={"voorbeeld@gmail.com"}
          />
          {initialEmail.trim() !== "" && (
                <Alert
                  type="info"
                  className="standard-text-color-light small-standard-margin-top small-standard-margin-bottom"
                    message="Dit e-mailadres is gekoppeld aan deze kaart. Vul de overige velden aan of wijzig ze. Bij activeren of bijwerken wordt een e-mail gestuurd."
                />
              )}
          <HorizontalLayout style={{ gap: "1rem" }}>
            <InputForm
              value={cardInfo.first_name}
              typeInput={(v) =>
                setCardInfo((prev) => ({ ...prev, first_name: v }))
              }
              title={"Voornaam (Optioneel)"}
            />
            <InputForm
              value={cardInfo.last_name}
              typeInput={(v) =>
                setCardInfo((prev) => ({ ...prev, last_name: v }))
              }
              title={"Achternaam (Optioneel)"}
            />
          </HorizontalLayout>
          {message && (
            <Alert
              type="error"
              className="standard-text-color-light small-standard-margin-top"
              message={message}
            />
          )}
          </>
          )}
        </ModalBody>
        <ModalFooter>
          {!waitingForMailCookie && <StandardButton
            onClick={dataToSave}
            className={"center"}
            size="large"
            style={{ margin: "0", width: "100%" }}
            title={"Activeer Kaart"}
          />}
        </ModalFooter> 
      </Modal>
    </>
  );
};
const Header = ({ waitingForMailCookie }) => {
  return (
    <>
      <VerticalLayout
        style={{ marginTop: "3rem" }}
        className="center standard-margin-bottom"
      >
        <IconAtom2
          color={"contactless-icon"}
          iconSize={70}
          icon2={"contactless"}
        />
        <ExpligoCard
          imageSize="clamp(3rem, 50vw, 5rem)"
          className="animate-tap"
          style={{ transform: "rotate(-12deg)", width: "clamp(5rem, 50vw, 7rem)", padding: "min(1rem, 1.5rem)" }}
        />
      </VerticalLayout>
      <VerticalLayout className="center" style={{ margin: "1rem 0" }}>
        <h4 style={{ textAlign: "center" }} className="standard-text-color active-title">
         {waitingForMailCookie? "Controleer je e-mail" : "Activeer je Expligo-kaart"}
        </h4>
        <p
          style={{ textAlign: "center" }}
          className="standard-text-color-light active-sub-title"
        >
         {waitingForMailCookie? "Er is een e-mail gestuurd. Klik op de link om je kaart te activeren." 
          : "Om je Tap-to-Learn traject te starten, activeer je je Expligo-kaart door hieronder je e-mailadres in te vullen."}
        </p>
      </VerticalLayout>
    </>
  );
};

const CardIdNotFound = ({ errorMessage }) => {
  return (
    <VerticalLayout className="center-all complete-height" style={{backgroundImage: `url(${ExpligoWP})`, }}>
      <Alert
        type="error"
        className="standard-text-color-light tap-to-learn-error"
        message={errorMessage || "Kaart-ID niet gevonden. Zorg ervoor dat je de juiste link gebruikt."}
      />
    </VerticalLayout>
  );
};
